/**
 * Permanents (indépendants) — main list page. Iter66.
 *
 * A dedicated page for the independent-contractor headcount, separate
 * from the salarié recrutement funnel (CEO 2026-07-30 iter66).
 * The list shows the current contract status + expiration countdown
 * so admin can quickly spot renewals due (J-7 heuristic).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    AlertTriangle,
    Copy,
    FileSignature,
    Plus,
    Search,
    UserCircle2,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "../lib/api";
import BirthdayModal from "../components/BirthdayModal";
import DeliveryNoteModal from "../components/DeliveryNoteModal";
import NewPermanentModal from "../components/permanents/NewPermanentModal";


function daysUntil(iso) {
    if (!iso) return null;
    const target = new Date(iso + "T00:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((target - today) / (1000 * 60 * 60 * 24));
}


function ExpiryPill({ endDate, status }) {
    if (!endDate) {
        return <span className="text-slate-400 text-[11px]">—</span>;
    }
    const days = daysUntil(endDate);
    let tone = "bg-slate-100 text-slate-600";
    let label = "";
    if (days < 0) { tone = "bg-red-50 text-red-700 border border-red-200"; label = `Expiré depuis ${-days} j`; }
    else if (days <= 7) { tone = "bg-amber-50 text-amber-700 border border-amber-200"; label = `Expire dans ${days} j`; }
    else if (days <= 30) { tone = "bg-blue-50 text-blue-700 border border-blue-200"; label = `Expire dans ${days} j`; }
    else { tone = "bg-emerald-50 text-emerald-700 border border-emerald-200"; label = `${days} j restants`; }
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${tone}`}>
            {days <= 7 && <AlertTriangle className="h-3 w-3" />}
            {label}
        </span>
    );
}


/**
 * CodeRdPill (iter70) — pill copiable qui affiche le "code recruteur"
 * (matricule AT+4 chiffres, ou exception ATO184). Un clic copie dans
 * le presse-papier avec un feedback toast. Utilisé partout où le code
 * doit être visible ET copiable — pipeline salarié comme prestataires.
 */
function CodeRdPill({ code }) {
    if (!code) {
        return <span className="text-slate-300 text-[11px] italic">—</span>;
    }
    const copy = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(code);
            toast.success(`Code ${code} copié`);
        } catch {
            toast.error("Copie impossible");
        }
    };
    return (
        <button
            onClick={copy}
            data-testid={`code-rd-pill-${code}`}
            title="Cliquer pour copier"
            className="inline-flex items-center gap-1.5 rounded-full bg-shariti-orange-soft border border-shariti-orange/40 text-shariti-orange px-2 py-0.5 text-[11px] font-black mono hover:bg-shariti-orange hover:text-white hover:border-shariti-orange transition"
        >
            {code}
            <Copy className="h-2.5 w-2.5 opacity-70" />
        </button>
    );
}


export default function Permanents() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    // iter110n · CEO 2026-09-27 · Modal d'envoi manuel du mail
    // d'anniversaire depuis la ligne PI (clic sur 🎂).
    const [birthdayModalPI, setBirthdayModalPI] = useState(null);
    // iter110n · CEO 2026-09-27 · Modal Bon de Remise depuis la ligne PI.
    const [deliveryModalPI, setDeliveryModalPI] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/permanents");
            setItems(data.items || []);
        } catch (e) {
            toast.error("Chargement des permanents impossible : " + (e?.response?.data?.detail || e.message));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        if (!search.trim()) return items;
        const q = search.trim().toLowerCase();
        return items.filter((p) =>
            (p.first_name + " " + p.last_name).toLowerCase().includes(q)
            || (p.email || "").toLowerCase().includes(q)
            || (p.siret || "").includes(q)
        );
    }, [items, search]);

    const soonExpiringCount = useMemo(() => {
        return items.filter((p) => {
            const end = p.current_contract?.end_date;
            const d = daysUntil(end);
            return d != null && d >= 0 && d <= 7;
        }).length;
    }, [items]);

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5" data-testid="permanents-page">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-4">
                    <div className="h-14 w-14 rounded-2xl bg-shariti-orange-soft text-shariti-orange flex items-center justify-center shrink-0">
                        <UserCircle2 className="h-6 w-6" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-black text-shariti-black tracking-tight">Permanents indépendants</h1>
                        <p className="text-[13px] text-slate-500 mt-1 max-w-xl leading-snug">
                            Prestataires autoentrepreneurs sous contrat de prestation de services.
                            Effectifs stables, hors funnel de recrutement salarié.
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {soonExpiringCount > 0 && (
                        <div
                            className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 px-3 py-1.5 text-[11px] font-bold"
                            data-testid="permanents-expiring-badge"
                        >
                            <AlertTriangle className="h-3.5 w-3.5" />
                            {soonExpiringCount} contrat{soonExpiringCount > 1 ? "s" : ""} à renouveler (≤7 j)
                        </div>
                    )}
                    <button
                        onClick={() => setModalOpen(true)}
                        data-testid="permanents-new-btn"
                        className="inline-flex items-center gap-2 rounded-full bg-shariti-orange text-white px-4 py-2 text-[12px] font-black uppercase tracking-widest hover:opacity-90 transition"
                    >
                        <Plus className="h-4 w-4" /> Nouveau permanent
                    </button>
                </div>
            </div>

            {/* Search */}
            <div className="card-soft p-3 flex items-center gap-2">
                <Search className="h-4 w-4 text-slate-400 ml-2" />
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher par nom, email, SIRET…"
                    data-testid="permanents-search"
                    className="flex-1 outline-none bg-transparent text-[13px] py-1.5"
                />
                <div className="text-[11px] text-slate-400 font-semibold px-2">
                    {filtered.length} / {items.length}
                </div>
            </div>

            {/* Table */}
            <div className="card-soft overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-slate-400 text-[13px]" data-testid="permanents-loading">Chargement…</div>
                ) : filtered.length === 0 ? (
                    <div className="p-12 text-center" data-testid="permanents-empty">
                        <UserCircle2 className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                        <div className="text-sm font-bold text-shariti-black">Aucun permanent enregistré</div>
                        <div className="text-[12px] text-slate-500 mt-1 max-w-sm mx-auto">
                            Ajoute un premier prestataire indépendant pour démarrer les effectifs.
                        </div>
                    </div>
                ) : (
                    /* iter110n · CEO 2026-09-22 · Mobile fix — le tableau
                       à 7 colonnes déborde sur mobile. On rend le
                       conteneur scrollable horizontalement (touch-friendly)
                       ET on garde la ligne cliquable en entier pour que
                       le CEO puisse taper le nom (colonne 1, toujours
                       visible) et aller directement sur la fiche pour
                       télécharger le badge. */
                    <div className="overflow-x-auto -mx-1">
                    <table className="w-full text-[13px] min-w-[720px]">
                        <thead className="bg-slate-50 border-b border-slate-100">
                            <tr className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                                <th className="text-left px-4 py-3">Prestataire</th>
                                <th className="text-left px-4 py-3">Code recruteur</th>
                                <th className="text-left px-4 py-3">SIRET</th>
                                <th className="text-left px-4 py-3">Région</th>
                                <th className="text-left px-4 py-3">Contrat actuel</th>
                                <th className="text-left px-4 py-3">Statut / Expire</th>
                                <th className="w-24"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((p) => (
                                // iter110n · CEO 2026-09-22 bugfix — sur mobile
                                // la dernière colonne "Ouvrir" est hors-écran
                                // (table à 7 colonnes) donc impossible d'accéder
                                // au profil pour télécharger le badge. Fix :
                                // toute la ligne est cliquable — la CEO tape
                                // n'importe où (nom, code, région…) et arrive
                                // sur `/permanents/{id}`. Le bouton "Ouvrir"
                                // reste visible sur desktop comme indice
                                // d'affordance.
                                <tr
                                    key={p.id}
                                    onClick={() => navigate(`/permanents/${p.id}`)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            navigate(`/permanents/${p.id}`);
                                        }
                                    }}
                                    className="border-b border-slate-100 hover:bg-slate-50/60 transition cursor-pointer active:bg-shariti-orange-soft/40 focus:outline-none focus:bg-shariti-orange-soft/30"
                                    data-testid={`permanent-row-${p.id}`}
                                >
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <div className="font-bold text-shariti-black">
                                                {p.civilite} {p.first_name} {p.last_name}
                                                {(p.prenom_usage || p.nom_usage) && (
                                                    <span className="ml-1 text-slate-400 font-semibold">
                                                        ({p.prenom_usage || p.first_name} {p.nom_usage || p.last_name})
                                                    </span>
                                                )}
                                            </div>
                                            {/* iter110n · CEO 2026-09-27 · Badge 🎂 anniversaire.
                                                Clic → modal d'envoi. Grisé + ✓ vert si déjà envoyé. */}
                                            {p.birthday_today && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setBirthdayModalPI({
                                                            permanent_id: p.id,
                                                            name: `${p.first_name} ${p.last_name}`.trim(),
                                                        });
                                                    }}
                                                    data-testid={`permanent-birthday-${p.id}`}
                                                    title={
                                                        p.birthday_greeting_sent_today
                                                            ? `🎂 ✓ Mail d'anniversaire déjà envoyé aujourd'hui à ${p.first_name}. Clique pour renvoyer.`
                                                            : `🎂 Anniversaire aujourd'hui — clique pour envoyer un mail à ${p.first_name}.`
                                                    }
                                                    className={`shrink-0 inline-flex items-center justify-center text-[14px] leading-none rounded-full transition-transform hover:scale-125 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-1 ${
                                                        p.birthday_greeting_sent_today
                                                            ? "opacity-50 saturate-50"
                                                            : "animate-bounce"
                                                    }`}
                                                    style={p.birthday_greeting_sent_today ? {} : { animationDuration: "2.4s" }}
                                                >
                                                    {p.birthday_greeting_sent_today ? (
                                                        <span className="relative">
                                                            🎂
                                                            <span className="absolute -bottom-0.5 -right-1 bg-emerald-500 text-white rounded-full w-2.5 h-2.5 flex items-center justify-center text-[7px] font-bold leading-none">
                                                                ✓
                                                            </span>
                                                        </span>
                                                    ) : (
                                                        "🎂"
                                                    )}
                                                </button>
                                            )}
                                            {/* iter110n · CEO 2026-09-27 · Bouton Bon de Remise Matériel.
                                                Clic → modal complet (compose + signatures + PDF). */}
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDeliveryModalPI({
                                                        permanent_id: p.id,
                                                        name: `${p.first_name} ${p.last_name}`.trim(),
                                                        default_type: "initial",
                                                    });
                                                }}
                                                data-testid={`permanent-delivery-note-${p.id}`}
                                                title={`📦 Créer un bon de remise de matériel pour ${p.first_name}`}
                                                className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-orange-100 hover:bg-orange-200 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-400"
                                            >
                                                <svg className="w-3.5 h-3.5 text-orange-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                                </svg>
                                            </button>
                                        </div>
                                        <div className="text-[11px] text-slate-500">{p.email}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <CodeRdPill code={p.code_rd} />
                                    </td>
                                    <td className="px-4 py-3 mono text-[12px] text-slate-700">{p.siret}</td>
                                    <td className="px-4 py-3">
                                        <span className="inline-block rounded-full bg-slate-100 text-slate-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">
                                            {(p.region || "").toUpperCase().replace(/_/g, " ") || "—"}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        {p.current_contract ? (
                                            <div>
                                                <div className="text-[12px] font-semibold text-shariti-black">
                                                    {p.current_contract.start_date} → {p.current_contract.end_date}
                                                </div>
                                                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                                                    {p.current_contract.origin === "uploaded" ? "Import signé" : "Généré PREREC"} · {p.current_contract.status}
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="text-slate-400 text-[12px] italic">Aucun contrat</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <ExpiryPill endDate={p.current_contract?.end_date} status={p.current_contract?.status} />
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <Link
                                            to={`/permanents/${p.id}`}
                                            onClick={(e) => e.stopPropagation()}
                                            data-testid={`permanent-open-${p.id}`}
                                            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1 text-[11px] font-bold text-slate-700 hover:bg-shariti-orange-soft hover:text-shariti-orange hover:border-shariti-orange-soft transition"
                                        >
                                            <FileSignature className="h-3 w-3" /> Ouvrir
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                )}
            </div>

            {modalOpen && (
                <NewPermanentModal
                    onClose={() => setModalOpen(false)}
                    onCreated={() => { setModalOpen(false); load(); }}
                />
            )}

            {/* iter110n · CEO 2026-09-27 · Modal d'envoi manuel du mail
                anniversaire pour PI (clic sur 🎂 dans la liste). */}
            <BirthdayModal
                open={!!birthdayModalPI}
                onOpenChange={(v) => !v && setBirthdayModalPI(null)}
                permanentId={birthdayModalPI?.permanent_id}
                candidateName={birthdayModalPI?.name}
                onSent={() => {
                    setBirthdayModalPI(null);
                    load();
                }}
            />

            {/* iter110n · CEO 2026-09-27 · Modal Bon de Remise Matériel. */}
            <DeliveryNoteModal
                open={!!deliveryModalPI}
                onOpenChange={(v) => !v && setDeliveryModalPI(null)}
                subjectType="permanent"
                subjectId={deliveryModalPI?.permanent_id}
                subjectName={deliveryModalPI?.name}
                defaultType={deliveryModalPI?.default_type || "initial"}
                onCompleted={() => {
                    // iter110n · Ne PAS fermer le modal ici — l'étape 4
                    // (SHA-256 + téléchargement PDF) doit rester visible.
                    // L'utilisateur ferme lui-même via le bouton « Fermer ».
                    load();
                }}
            />
        </div>
    );
}
